import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { categories } from '../data/categories';
import { sortOptions, type SortKey } from '../data/shopFilters';
import { useShopFilters } from '../hooks/useShopFilters';
import { FilterDrawer } from '../components/shop/FilterDrawer';
import { ShopFilters } from '../components/shop/ShopFilters';
import { ProductGrid } from '../components/product/ProductGrid';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { btnSecondary } from '../components/ui/buttonStyles';
import { getCategory } from '../utils/catalog';
export function Shop() {
  const filters = useShopFilters();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const category = filters.category ? getCategory(filters.category) : undefined;
  const title = category?.name ?? (filters.leavingOnly ? 'Derniers jours' : 'La boutique');
  const description = category?.description ?? (filters.leavingOnly ? 'Les créateurs qui quittent la vitrine dans les trois prochaines semaines. Après leur départ, ces pièces ne seront plus en vente.' : 'Toutes les pièces des créateurs actuellement en vitrine. La sélection change au fil des arrivées et des départs.');
  const tabs = [{
    id: null,
    label: 'Tout'
  }, ...categories.map(c => ({
    id: c.id,
    label: c.name
  }))];
  return <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-8 md:px-8 md:pt-12" data-id="element-950" data-mp="2ulz1h1v61m8g">
      <Breadcrumbs items={[{
      label: 'Accueil',
      to: '/'
    }, {
      label: 'Boutique',
      to: category ? '/boutique' : undefined
    }, ...(category ? [{
      label: category.name
    }] : [])]} data-id="element-951" data-mp="2zkitnk0cru99" />
      <header className="mt-6 max-w-3xl" data-id="element-952" data-mp="33o8djw631nny">
        <h1 className="font-display text-5xl leading-[0.98] md:text-7xl" data-id="element-953" data-mp="38lqeaz9y4wkv">{title}</h1>
        <p className="mt-4 text-base text-muted md:text-lg" data-id="element-954" data-mp="iw56rj5zx5rx">{description}</p>
      </header>

      <nav aria-label="Univers" className="no-scrollbar -mx-5 mt-10 overflow-x-auto border-b border-line px-5 md:mx-0 md:px-0" data-id="element-955" data-mp="nuznmfkekk9e">
        <ul className="flex gap-7" data-id="element-956" data-mp="2jxcudffgen7x">
          {tabs.map(t => {
          const active = filters.category === t.id;
          const count = filters.categoryCounts[t.id ?? 'all'];
          return <li key={t.label} className="shrink-0" data-id="element-957" data-mp="2tv93y2sqta3x">
                <button type="button" onClick={() => filters.setCategory(t.id)} aria-current={active ? 'page' : undefined} className={`relative whitespace-nowrap pb-3 text-sm transition-colors duration-150 ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-958" data-mp="2ox7k3fnht6re">
                  {t.label} <span className="text-xs tabular-nums" data-id="element-959" data-mp="2az0u0loscb2a">({count})</span>
                  {active && <motion.span layoutId="shop-tab" className="absolute inset-x-0 -bottom-px h-0.5 bg-ink" transition={{
                duration: 0.25,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-960" data-mp="265eqpk4ihjj5" />}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-id="element-961" data-mp="3p4cl3z6po76d">
        <p className="text-sm text-muted" aria-live="polite" data-id="element-962" data-mp="3twdvtgo8dpua">
          {filters.results.length} pièce{filters.results.length > 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2" data-id="element-963" data-mp="3g2mmo9eew7z3">
          <button type="button" onClick={() => setDrawerOpen(true)} className={`${btnSecondary} h-10 px-4 lg:hidden`} data-id="element-964" data-mp="3k5jbihujp0bh">
            <SlidersHorizontalIcon className="h-4 w-4" data-id="element-965" data-mp="brsowy4hm24f" /> Filtrer
            {filters.activeFilters.length > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-clay px-1 text-[11px] text-paper" data-id="element-966" data-mp="goswjdgzjcxn">
                {filters.activeFilters.length}
              </span>}
          </button>
          <label htmlFor="sort" className="sr-only" data-id="element-967" data-mp="2ng1xnfles7yp">
            Trier par
          </label>
          <select id="sort" value={filters.sort} onChange={e => filters.setSort(e.target.value as SortKey)} className="h-10 rounded-full border border-ink/25 bg-transparent px-4 text-sm outline-none focus:border-ink" data-id="element-968" data-mp="2sf4zkr63dy2g">
            {sortOptions.map(o => <option key={o.value} value={o.value} data-id="element-969" data-mp="2xygumz3tmll9">
                {o.label}
              </option>)}
          </select>
        </div>
      </div>

      {filters.activeFilters.length > 0 && <ul className="mt-4 flex flex-wrap gap-2" aria-label="Filtres actifs" data-id="element-970" data-mp="3qnw4hljr28fl">
          {filters.activeFilters.map(f => <li key={f.key} data-id="element-971" data-mp="3lp2p1nkwk7cs">
              <button type="button" onClick={f.onRemove} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-ink px-3 text-xs text-paper transition-colors duration-150 hover:bg-clay" aria-label={`Retirer le filtre ${f.label}`} data-id="element-972" data-mp="3lmq9fh2ma92w">
                {f.label} <XIcon className="h-3 w-3" data-id="element-973" data-mp="2186h1fu583zu" />
              </button>
            </li>)}
        </ul>}

      <div className="mt-8 grid gap-10 lg:grid-cols-[230px_1fr]" data-id="element-974" data-mp="grgjgnczimjv">
        <aside className="hidden self-start lg:sticky lg:top-28 lg:block" aria-label="Filtres" data-id="element-975" data-mp="lprsivvggt11">
          <ShopFilters filters={filters} data-id="element-976" data-mp="prdcf16eq7kq" />
        </aside>
        <div data-id="element-977" data-mp="ppsh39c9qklt">
          {filters.results.length > 0 ? <ProductGrid products={filters.results} columns={3} data-id="element-978" data-mp="uill1uzwxowu" /> : <div className="flex flex-col items-center rounded-sm bg-card px-6 py-20 text-center" data-id="element-979" data-mp="zh57dwif52pq">
              <p className="font-display text-3xl" data-id="element-980" data-mp="14g9f0ipp2sdy">Rien ne correspond… pour l’instant</p>
              <p className="mt-2 max-w-md text-sm text-muted" data-id="element-981" data-mp="14i2pya7cbyrr">
                De nouveaux créateurs arrivent chaque mois. Élargissez vos filtres ou revenez bientôt.
              </p>
              <button type="button" onClick={filters.resetFilters} className={`${btnSecondary} mt-6`} data-id="element-982" data-mp="1detsn5geebvb">
                Effacer les filtres
              </button>
            </div>}
        </div>
      </div>

      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} filters={filters} data-id="element-983" data-mp="3fa43tfjuhgtu" />
    </div>;
}